import { Payroll, PayrollCycleType } from "@wingspanhq/wingspan-api-sdk";
import sumBy from "lodash/sumBy";

import { PayrollRunDetails } from "../types";
import {
  formatPayPeriodLabel,
  formatPayPeriodRange,
  parsePayrollAmount
} from "../utils";

import { selectorPayrollRunStatus } from "./selectorPayrollRunStatus";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Build the run details view model from a v3 `Payroll`.
 *
 * Run-level money rollups come from `Payroll.breakdown`, which the API only
 * populates when the read passes `expand: [PayrollExpand.BREAKDOWN]` (see
 * `RoutePayrollRunsDetails`). The module runs Employee-type payroll
 * (`PAYROLL_RUNS_TYPE`), so figures are read from `employeeBreakdown`: `grossPay`,
 * the summed employer `payerTaxes`, `grossLiabilityAmount` (mirrors the run's
 * `totalAmount`), and `payStatementsCount`. `benefits` has no v3 breakdown field
 * yet, so it stays 0. `paystubs` is owned by the paystub list (rendered via
 * `FilterableTablePaystubs`), so it is left empty here.
 */
export const selectorPayrollRunDetails = (
  payroll: Payroll
): PayrollRunDetails => {
  const status = selectorPayrollRunStatus(payroll.status);
  const breakdown = payroll.breakdown?.employeeBreakdown;

  // `grossLiabilityAmount` mirrors the run's top-level `totalAmount`; fall back
  // to `totalAmount` when the breakdown was not expanded.
  const liabilityAmount =
    breakdown?.grossLiabilityAmount ?? payroll.totalAmount;

  const worklogApprovalDueDate = payroll.approvalDeadline ?? undefined;
  const worklogApprovalDaysLeft = worklogApprovalDueDate
    ? Math.max(
        Math.ceil((worklogApprovalDueDate.getTime() - Date.now()) / MS_PER_DAY),
        0
      )
    : undefined;

  // The worklog-approval alert only applies while the run is still open/pending.
  const isApprovalWindowOpen = status === "pending" || status === "opened";

  return {
    id: payroll.id ?? "",
    payPeriodLabel: formatPayPeriodLabel(payroll.payPeriodStartDate),
    payPeriodRange: formatPayPeriodRange(
      payroll.payPeriodStartDate,
      payroll.payPeriodEndDate
    ),
    isOffCycle: payroll.cycleType === PayrollCycleType.OFF_CYCLE,
    liabilityAmount:
      liabilityAmount === undefined
        ? undefined
        : parsePayrollAmount(liabilityAmount),
    grossAmount: parsePayrollAmount(breakdown?.grossPay),
    payerTaxes: sumBy(breakdown?.payerTaxes ?? [], entry =>
      parsePayrollAmount(entry.amount)
    ),
    benefits: 0,
    payrollId: payroll.payrollNumber ?? payroll.id ?? "",
    finalizationDate: payroll.events?.finalizedAt ?? undefined,
    payDate: payroll.payDate,
    payrollStartDate: payroll.payPeriodStartDate ?? new Date(),
    status,
    paystubsCount: breakdown?.payStatementsCount ?? 0,
    paystubs: [],
    worklogApprovalDueDate: isApprovalWindowOpen
      ? worklogApprovalDueDate
      : undefined,
    worklogApprovalDaysLeft: isApprovalWindowOpen
      ? worklogApprovalDaysLeft
      : undefined
  };
};
